import { useCallback, useId, useState } from 'react'
import * as uploadService from '../../services/uploadService'
import { UploadProgressBar } from './UploadProgressBar'

export type ImageUploadFieldProps = {
  label: string
  hint?: string
  multiple?: boolean
  accept?: string
  disabled?: boolean
  onUploaded: (urls: string[]) => void
  onUploadError?: (message: string) => void
  onUploadStateChange?: (uploading: boolean) => void
}

/**
 * Reusable image picker + upload to `/uploads/images` with a determinate progress bar.
 * Platform logo flows use `/admin/uploads/logo` instead (see organization form).
 */
export function ImageUploadField({
  label,
  hint,
  multiple = true,
  accept = 'image/png,image/jpeg,image/webp,image/gif',
  disabled = false,
  onUploaded,
  onUploadError,
  onUploadStateChange,
}: ImageUploadFieldProps) {
  const id = useId()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [dragOver, setDragOver] = useState(false)

  const runUpload = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files?.length || disabled) return
      let list = Array.from(files)
      if (!multiple) {
        list = list.slice(0, 1)
      }
      if (list.length === 0) return

      setUploading(true)
      onUploadStateChange?.(true)
      setProgress(0)
      try {
        const urls = await uploadService.uploadProductImages(list, setProgress)
        onUploaded(urls)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        onUploadError?.(msg)
      } finally {
        setUploading(false)
        onUploadStateChange?.(false)
        setProgress(0)
      }
    },
    [disabled, multiple, onUploaded, onUploadError, onUploadStateChange],
  )

  return (
    <div className="w-full">
      <span className="mb-2 block text-sm font-medium text-foreground">{label}</span>
      <div
        className={`group relative overflow-hidden rounded-2xl border border-dashed bg-gradient-to-b from-muted/80 to-card px-4 py-5 transition-all duration-200 hover:shadow-[inset_0_1px_0_rgb(255_255_255_/_0.06)] ${
          dragOver
            ? 'border-primary bg-primary/10 shadow-[inset_0_0_0_1px_var(--color-ring)]'
            : 'border-border hover:border-primary/40 hover:from-primary/5'
        } ${uploading || disabled ? 'pointer-events-none opacity-60' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled && !uploading) setDragOver(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setDragOver(false)
          if (disabled || uploading) return
          const dt = e.dataTransfer
          if (dt?.files?.length) void runUpload(dt.files)
        }}
      >
        <input
          id={id}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled || uploading}
          className="sr-only"
          onChange={(e) => {
            void runUpload(e.target.files)
            e.target.value = ''
          }}
        />
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <p className="text-sm font-medium text-foreground">Drop images here or browse</p>
            {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          <label
            htmlFor={id}
            className={`inline-flex cursor-pointer items-center justify-center rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all duration-150 hover:border-primary/30 hover:bg-primary/10 hover:text-foreground ${disabled || uploading ? 'pointer-events-none opacity-45' : ''}`}
          >
            Choose file{multiple ? 's' : ''}
          </label>
        </div>
        {uploading ? (
          <div className="mt-4 border-t border-border pt-4">
            <UploadProgressBar active={uploading} progress={progress} label="Uploading…" />
          </div>
        ) : null}
      </div>
    </div>
  )
}
